import React from 'react';
import {useFragment} from "react-relay";
import graphql from "babel-plugin-relay/macro";
import {useUser} from "../../hooks/useUser";
import {Button, Card, Col, ListGroup, ListGroupItem, Row} from "react-bootstrap";
import CardTableHeader from "../common/headers/CardTableHeader";
import NewNoteModal from "./NewNoteModal";
import EditNoteModal from "./EditNoteModal";
import DeleteModal from "../common/modals/DeleteModal";
import CardDropdown from "../common/CardDropdown";
import DropdownItem from "react-bootstrap/DropdownItem";
import {useDataModals} from "../../hooks/useDataModals";
import UsePagination from "../../hooks/usePagination";
import Flex from "../common/Flex";
import classNames from "classnames";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";

const Notes = ({notes, parentId, createPermission, editPermission, deletePermission, paginationSize}) => {
    const noteData = useFragment(graphql`
    fragment Notes on Note @relay(plural:true) {
        id
        content
        createdByUser {
            userName
        }
        lastModifiedByUser {
            userName
        }
        lastModifiedTimestampUtc
        createdTimestampUtc
    }`, notes).sort((a, b) => {
        let aDate = dayjs(a.createdTimestampUtc).utc()
        let bDate = dayjs(b.createdTimestampUtc).utc()
        if(aDate.isSame(bDate)) return 0;
        return aDate.isAfter(bDate) ? 1 : -1;
        
    })
    const {paginationState: {data, currentPage, totalPage, canNextPage, canPreviousPage}, nextPage, prevPage, goToPage} = UsePagination(noteData, paginationSize);
    const deleteMutation = graphql`
    mutation NotesDeleteMutation($id: ID!) {
        deleteNode(input: {id: $id}) {
            deletedNodeId @deleteRecord
            owner {
                ... on NetworkOwner {
                    notes {
                        ...Notes
                    }
                }
                ... on LegalContract {
                    notes {
                        ...Notes
                    }
                }
                ... on Organization {
                    notes {
                        ...Notes
                    }
                }
                ... on Network {
                    notes {
                        ...Notes
                    }
                }
            }
        }}`
    const {hasPermission} = useUser();
    const {selected, newModal, editModal, deleteModal} = useDataModals()
    
    return (
        <>
            <Card className={"h-100"}>
                <Card.Header>
                <CardTableHeader title={"Quick Notes"} onNewClick={newModal.onClick} showNew={hasPermission(createPermission)} />
                </Card.Header>
                    <ListGroup className={"bg-light border-top flex-grow-1"}>
                        {data.map((d) => {
                            return <ListGroupItem key={d.id}>
                                <Row>
                                    <Col>
                                        <p>{d.content}</p>
                                    </Col>
                                    {(hasPermission(editPermission) || hasPermission(deletePermission)) &&
                                    <Col xs={4} sm={"auto"} className={"d-flex align-items-center"}>
                                        <CardDropdown>
                                            {hasPermission(editPermission) && <DropdownItem as={"button"} onClick={() => {editModal.onClick(d)}}>Edit</DropdownItem>}
                                            {hasPermission(deletePermission) && <DropdownItem as={"button"} onClick={() => {deleteModal.onClick(d)}}>Delete</DropdownItem>}
                                        </CardDropdown>
                                    </Col>
                        }
                                </Row>
                            </ListGroupItem>
                        })}
                </ListGroup>
                <Card.Footer className="border-top">
                    <Flex alignItems={"center"} justifyContent={"center"}>
                        <Button
                            size="sm"
                            variant="falcon-default"
                            onClick={() => prevPage()}
                            aria-label={"Go to previous page in list"}
                            className={classNames({ disabled: !canPreviousPage })}
                        >
                            <FontAwesomeIcon icon="chevron-left" />
                        </Button>
                        <ul className="pagination mb-0 mx-2">
                            {Array.from(Array(totalPage).keys()).map((page, index) => (
                                <li key={page} className={classNames({ active: currentPage === page + 1})}>
                                    <Button
                                        size="sm"
                                        variant="falcon-default"
                                        className={classNames('page', {
                                            'me-2': index + 1 !== totalPage
                                        })}
                                        aria-label={`Go to page ${index + 1} in table`}
                                        onClick={() => goToPage(page + 1)}
                                    >
                                        {page + 1}
                                    </Button>
                                </li>
                            ))}
                        </ul>
                        <Button
                            size="sm"
                            variant="falcon-default"
                            onClick={() => nextPage()}
                            className={classNames({ disabled: !canNextPage })}
                            aria-label={"Go to next page in list"}
                        >
                            <FontAwesomeIcon icon="chevron-right" />
                        </Button>
                    </Flex>
                </Card.Footer>
            </Card>
            <NewNoteModal showModal={newModal.showModal}
                          onClose={newModal.onClose}
                          parentId={parentId}
            />
            {selected && <EditNoteModal 
                showModal={editModal.showModal} 
                onClose={editModal.onClose} 
                note={selected} />}
            {selected && <DeleteModal mutation={deleteMutation}
                         onClose={deleteModal.onClose}
                         thingToDelete={"Note"}
                         showModal={deleteModal.showModal} id={selected.id}
            >
                <p>This cannot be undone</p>
            </DeleteModal>}
        </>
    )
}
export default Notes
